
import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
    MissingTranslationHandler,
    TranslateLoader,
    TranslateModule,
    TranslateService
} from '@ngx-translate/core';
import { BookingCancelationComponent } from './components/booking-cancelation/booking-cancelation.component';
import { CancellationReasonPopupComponent } from './components/cancellation-reason-popup/cancellation-reason-popup.component';
import { BookingUserDetailsComponent } from './components/booking-user-details/booking-user-details.component';
import { BookingDriverDetailsComponent } from './components/driver-details/booking-driver-details.component';
import { NavtabComponent } from './components/navtab/navtab.component';
import { TripMapComponent } from './components/trip-map/trip-map.component';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { ConfirmationService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ChartModule } from 'primeng/chart';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { EditorModule } from 'primeng/editor';
import { FileUploadModule } from 'primeng/fileupload';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PanelMenuModule } from 'primeng/panelmenu';
import { RadioButtonModule } from 'primeng/radiobutton';
import { RatingModule } from 'primeng/rating';
import { SidebarModule } from 'primeng/sidebar';
import { TabMenuModule } from 'primeng/tabmenu';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';

import { FieldsetModule } from 'primeng/fieldset';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { PasswordModule } from 'primeng/password';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { SplitButtonModule } from 'primeng/splitbutton';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { AssignDriverComponent } from './components/assign-driver/assign-driver.component';
import { AssignSearchComponent } from './components/assign-search/assign-search.component';
import { BarChartComponent } from './components/bar-chart/bar-chart.component';
import { BookingHistoryDetailsComponent } from './components/booking-history-details/booking-history-details.component';
import { BreadCrumbsComponent } from './components/bread-crumbs/bread-crumbs.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { CommentInfoComponent } from './components/comment-info/comment-info.component';
import { CommentReplyComponent } from './components/comment-reply/comment-reply.component';
import { DashboardActivityComponent } from './components/dashboard-activity/dashboard-activity.component';
import { DashboardStatsCountsComponent } from './components/dashboard-stats-counts/dashboard-stats-counts.component';
import { DevelopementInProgressComponent } from './components/developement-in-progress/developement-in-progress.component';
import { AccessDeniedComponent } from './components/access-denied/access-denied.component';
import { AddEditFormSkeletonComponent } from './components/add-edit-form-skeleton/add-edit-form-skeleton.component';
import { ViewDetailsSkeletonComponent } from './components/view-details-skeleton/view-details-skeleton.component';
import { EditProfileComponent } from './components/edit-profile/edit-profile.component';
import { FileUploaderComponent } from './components/file-uploader/file.uploader.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { HeaderComponent } from './components/header/header.component';
import { LoaderComponent } from './components/loader/loader.component';
import { LoaderService } from './components/loader/loader.service';
import { LogDeailsComponent } from './components/log-deails/log-deails.component';
import { MergeBookingPopupComponent } from './components/merge-booking-popup/merge-booking-popup.component';
import { MessageComponent } from './components/message/message.component';
import { MiFilterComponent } from './components/mi-filter/mi-filter.component';
import { MiGrid2Component } from './components/mi-grid-2/mi-grid-2.component';
import { NotificationMG1Component } from './components/notification-mg1/notification-mg1.component';
import { OtpVerifiyComponent } from './components/otp-verifiy/otp-verifiy.component';
import { OtpComponent } from './components/otp/otp.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { PassengerDayBookingsComponent } from './components/passenger-day-bookings/passenger-day-bookings.component';
import { PieChartComponent } from './components/pie-chart/pie-chart.component';
import { RecentEmergencyRequestComponent } from './components/recent-emergency-request/recent-emergency-request.component';
import { SetPasswordComponent } from './components/set-password/set-password.component';
import { SendPasswordLinkDialogComponent } from './components/send-password-link-dialog/send-password-link-dialog.component';
import { AssignDriverV2Component } from './components/assign-driver-v2/assign-driver-v2.component';
import { AssignDriverMapComponent } from './components/assign-driver-map/assign-driver-map.component';
import { UpcomingBookingsComponent } from './components/upcoming-bookings/upcoming-bookings.component';
import { SidebarAssignDriverComponent } from './components/sidebar-assign-driver/sidebar-assign-driver.component';
import { SidebarBookingDetailsComponent } from './components/sidebar-booking-details/sidebar-booking-details.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { TotalAndActiveCountsComponent } from './components/total-and-active-counts/total-and-active-counts.component';
import { TrackLocationComponent } from './components/track-location/track-location.component';
import { UpdatePasswordComponent } from './components/update-password/update-password.component';
import { AccessProviderDirective } from './directives/access-provider.directive';
import { DisableControlDirective } from './directives/input-disabled.directive';
import { LogicalAccessProviderDirective } from './directives/logical-access-provider.directive';
import { RippleDirective } from './directives/ripple.directive';
import { SearchLocationDirective } from './directives/search-location.directive';
import { ImagePreviewDirective } from './directives/image-preview.directive';
import { TwoDigitNumberDirective } from './directives/two-digit-number.directive';
import { ImagePreviewDialogComponent } from './components/image-preview-dialog/image-preview-dialog.component';
import { AgoPipe } from './pipes/ago.pipe';
import { DateFormatPipe } from './pipes/dateformat.pipe';
import { DateTimeFormatPipe } from './pipes/datetimeformat.pipe';
import { KeepHtmlPipe } from './pipes/keep-html.pipe';
import { MiliSecondToMinutesPipe } from './pipes/miliSecondToMinutes.pipe';
import { MinutsFormatPipe } from './pipes/minutesformat.pipe';
import { SlotsTimeOnlyPipe } from './pipes/slots-time-only.pipe';
import { AuthenticationValidationService } from './services/authentication-validation.service';
import { CommonBindingDataService } from './services/common-binding-data.service';
import { ErrorBindingService } from './services/error-binding.service';
import { MiMissingTranslationHandlerService } from './services/mi-missing-translation-handler.service';
import { MiTranslateLoaderService } from './services/mi-translate-loader.service';
import { RestApiService } from './services/rest-api.service';
import { PageHeaderService } from './services/page-header.service';
import { SidebarService } from './services/sidebar.service';
import { StorageService } from './services/storage.service';
import { UiService } from './services/ui.service';
import { UserCommonService } from './services/user-common.service';
import { FileUploaderLatestComponent } from './components/file-uploader-latest/file.uploader-latest.component';
import { NoRecordComponent } from './components/no-record/no-record.component';

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    PanelMenuModule,
    RatingModule,
    ButtonModule,
    EditorModule,
    ConfirmDialogModule,
    CalendarModule,
    ChartModule,
    MenuModule,
    DropdownModule,
    RadioButtonModule,
    CheckboxModule,
    FieldsetModule,
    DialogModule,
    FormsModule,
    FileUploadModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    TableModule,
    SkeletonModule,
    PaginatorModule,
    BlockUIModule,
    OverlayPanelModule,
    TabMenuModule,
    TabViewModule,
    SidebarModule,
    PasswordModule,
    InputSwitchModule,
    InputTextModule,
    ToastModule,
    MultiSelectModule,
    AccordionModule,
    SelectButtonModule,
    ToggleButtonModule,
    SplitButtonModule,
    OverlayPanelModule,
    // KeyFilterModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: MiTranslateLoaderService
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MiMissingTranslationHandlerService
      }
    }),
  ],
  declarations: [
    HeaderComponent,
    SidebarComponent,
    PageHeaderComponent,
    DisableControlDirective,
    SearchLocationDirective,
    ChangePasswordComponent,
    EditProfileComponent,
    KeepHtmlPipe,
    AssignSearchComponent,
    PieChartComponent,
    BarChartComponent,
    DashboardStatsCountsComponent,
    DashboardActivityComponent,
    LoaderComponent,
    FileUploaderComponent,
    FileUploaderLatestComponent,
    AgoPipe,
    MiliSecondToMinutesPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    MinutsFormatPipe,
    SlotsTimeOnlyPipe,
    TotalAndActiveCountsComponent,
    BookingDriverDetailsComponent,
    BookingUserDetailsComponent,
    TripMapComponent,
    NotificationMG1Component,
    CommentInfoComponent,
    CommentReplyComponent,
    ForgotPasswordComponent,
    OtpVerifiyComponent,
    BookingHistoryDetailsComponent,
    OtpVerifiyComponent,
    SetPasswordComponent,
    SendPasswordLinkDialogComponent,
    NavtabComponent,
    BookingCancelationComponent,
    CancellationReasonPopupComponent,
    RecentEmergencyRequestComponent,
    OtpComponent,
    TrackLocationComponent,
    AccessProviderDirective,
    MiGrid2Component,
    MiFilterComponent,
    AssignDriverComponent,
    LogDeailsComponent,
    MessageComponent,
    SidebarAssignDriverComponent,
    BreadCrumbsComponent,
    UpdatePasswordComponent,
    DevelopementInProgressComponent,
    AccessDeniedComponent,
    AddEditFormSkeletonComponent,
    ViewDetailsSkeletonComponent,
    RippleDirective,
    SidebarBookingDetailsComponent,
    PassengerDayBookingsComponent,
    LogicalAccessProviderDirective,
    MergeBookingPopupComponent,
    AssignDriverV2Component,
    AssignDriverMapComponent,
    UpcomingBookingsComponent,
    ImagePreviewDirective,
    TwoDigitNumberDirective,
    ImagePreviewDialogComponent,
    NoRecordComponent
  ],
  exports: [
    DropdownModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    SidebarComponent,
    PageHeaderComponent,
    TranslateModule,
    ConfirmDialogModule,
    DialogModule,
    SearchLocationDirective,
    AccessProviderDirective,
    DisableControlDirective,
    AssignSearchComponent,
    PieChartComponent,
    BarChartComponent,
    DashboardStatsCountsComponent,
    DashboardActivityComponent,
    LoaderComponent,
    FileUploaderComponent,
    FileUploaderLatestComponent,
    AgoPipe,
    MiliSecondToMinutesPipe,
    DateFormatPipe,
    DateTimeFormatPipe,
    KeepHtmlPipe,
    MinutsFormatPipe,
    SlotsTimeOnlyPipe,
    TotalAndActiveCountsComponent,
    BookingDriverDetailsComponent,
    BookingUserDetailsComponent,
    TripMapComponent,
    NotificationMG1Component,
    CommentInfoComponent,
    ForgotPasswordComponent,
    OtpVerifiyComponent,
    BookingHistoryDetailsComponent,
    SetPasswordComponent,
    SendPasswordLinkDialogComponent,
    NavtabComponent,
    BookingCancelationComponent,
    CancellationReasonPopupComponent,
    RecentEmergencyRequestComponent,
    OtpComponent,
    MiGrid2Component,
    MiFilterComponent,
    TrackLocationComponent,
    AssignDriverComponent,
    LogDeailsComponent,
    MessageComponent,
    SidebarAssignDriverComponent,
    BreadCrumbsComponent,
    UpdatePasswordComponent,
    RippleDirective,
    DevelopementInProgressComponent,
    AccessDeniedComponent,
    AddEditFormSkeletonComponent,
    ViewDetailsSkeletonComponent,
    MultiSelectModule,
    AccordionModule,
    SelectButtonModule,
    SidebarBookingDetailsComponent,
    PassengerDayBookingsComponent,
    LogicalAccessProviderDirective,
    TabMenuModule,
    CheckboxModule,
    CalendarModule,
    FieldsetModule,
    AutoCompleteModule,
    TableModule,
    SkeletonModule,
    SplitButtonModule,
    MergeBookingPopupComponent,
    AssignDriverV2Component,
    AssignDriverMapComponent,
    UpcomingBookingsComponent,
    ImagePreviewDirective,
    TwoDigitNumberDirective,
    ImagePreviewDialogComponent,
    NoRecordComponent,
    // KeyFilterModule
  ]
})
export class SharedModule {

  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        LoaderService,
        RestApiService,
        UiService,
        UserCommonService,
        StorageService,
        ErrorBindingService,
        CommonBindingDataService,
        SearchLocationDirective,
        DisableControlDirective,
        AuthenticationValidationService,
        ConfirmationService,
        TranslateService,
        PageHeaderService,
        SidebarService,
      ]
    };
  }

  constructor(translate: TranslateService) {
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('en');
    // the lang to use, if the lang isn't available, it will use the current loader to get them
    translate.use('en');

  }

}
